import React from 'react';
import { Button, KIND as ButtonKind, SHAPE, SIZE } from 'baseui/button';
import { HeadingSmall, LabelSmall, LabelMedium } from 'baseui/typography';
import { Delete, Plus, Overflow } from 'baseui/icon';
import { useNavigate, Outlet } from 'react-router-dom';
import useStore from '../store';
import { MethodTag } from './MethodTag';

const OperationCard = ({ operation, onClick, onDelete }) => {
  return (
    <div
      style={{
        minHeight: '160px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '12px',
        position: 'relative'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '0',
          right: '0',
          display: 'flex',
          gap: '4px'
        }}
      >
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          kind={ButtonKind.tertiary}
          shape={SHAPE.round}
          size={SIZE.mini}
          title="Delete Operation"
        >
          <Delete size={20} />
        </Button>
      </div>
      <div style={{ marginTop: '32px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}
        >
          <MethodTag method={operation.method} />
          <LabelSmall
            color="contentSecondary"
            style={{
              fontSize: '12px',
              fontFamily: 'monospace',
              margin: 0,
              lineHeight: '1.4'
            }}
          >
            {operation.path}
          </LabelSmall>
        </div>
        <HeadingSmall
          margin="0"
          style={{
            fontSize: '16px',
            lineHeight: '1.4'
          }}
        >
          {operation.name}
        </HeadingSmall>
      </div>
    </div>
  );
};

const NewOperationCard = ({ onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        minHeight: '160px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ':hover': {
          borderColor: '#CBD5E0',
          backgroundColor: '#F7FAFC'
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '50%',
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease'
        }}
      >
        <Plus size={24} />
      </div>
      <HeadingSmall
        margin="0"
        style={{
          fontSize: '16px',
          fontWeight: 500
        }}
      >
        Add Operation
      </HeadingSmall>
    </div>
  );
};

export function ServiceList() {
  const store = useStore();
  const navigate = useNavigate();
  const [error, setError] = React.useState(null);
  const operationsLoaded = React.useRef({});
  const servicesFetched = React.useRef(false);
  const operationsFetchFailed = React.useRef(new Set()); // Track failed operation fetches

  // Reset state when component unmounts
  React.useEffect(() => {
    const failedOps = operationsFetchFailed.current;
    return () => {
      servicesFetched.current = false;
      operationsLoaded.current = {};
      failedOps.clear();
      setError(null);
    };
  }, []);

  // Clear error when services change
  React.useEffect(() => {
    setError(null);
  }, [store.services]);

  // Memoize the loadData function
  const loadData = React.useCallback(async () => {
    try {
      setError(null);
      // Load services if needed
      if (!store.services?.length && !servicesFetched.current && !store.servicesLoading) {
        servicesFetched.current = true;
        await store.fetchServices();
        return; // Let the next effect cycle handle operations
      }

      // Load operations for each service that hasn't been loaded
      if (store.services?.length) {
        const promises = store.services.map(async (service) => {
          if (service && service.id && 
              !store.serviceOperations?.[service.id] && 
              !store.serviceOperationsLoading?.[service.id] &&
              !operationsLoaded.current[service.id] &&
              !operationsFetchFailed.current.has(service.id)) {
            operationsLoaded.current[service.id] = true;
            try {
              await store.fetchServiceOperations(service.id);
              // If we got here, the operation fetch succeeded
              operationsFetchFailed.current.delete(service.id);
            } catch (error) {
              // If fetching operations fails, mark it as failed to prevent retrying
              operationsFetchFailed.current.add(service.id);
              console.error(`Failed to fetch operations for service ${service.id}:`, error);
              // Don't throw error for no operations
              if (error.message !== 'Service not found') {
                throw error;
              }
            }
          }
        });
        await Promise.all(promises);
      }
    } catch (error) {
      console.error('Error in loadData:', error);
      setError(error.message || 'Failed to load data');
    }
  }, [store]);

  // Load services and their operations
  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditService = (e, service) => {
    e.stopPropagation();
    store.setSelectedService(service);
    navigate(`/services/${service.id}/edit`);
  };

  const handleNewOperation = (serviceId) => {
    store.setSelectedOperation({ serviceId });
    navigate('/services/operations/new');
  };

  const handleEditOperation = (operation) => {
    store.setSelectedOperation(operation);
    navigate(`/services/operations/${operation.id}`);
  };

  const handleNewService = () => {
    store.setSelectedService({});
    navigate('/services/new');
  };

  const handleDeleteService = async (e, service) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete service "${service.name}"?`)) {
      try {
        setError(null);
        await store.deleteService(service.id);
        // Reset fetch flags to trigger a refresh
        servicesFetched.current = false;
        operationsLoaded.current = {};
      } catch (error) {
        setError(error.message || 'Failed to delete service');
        console.error('Failed to delete service:', error);
      }
    }
  };

  const handleDeleteOperation = async (operation) => {
    if (window.confirm(`Are you sure you want to delete operation "${operation.path}"?`)) {
      try {
        setError(null);
        await store.deleteOperation(operation.serviceId, operation.id);
        // Reset operation loaded flag to trigger a refresh
        delete operationsLoaded.current[operation.serviceId];
      } catch (error) {
        setError(error.message || 'Failed to delete operation');
        console.error('Failed to delete operation:', error);
      }
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '24px',
        flex: 1,
        height: '100%'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          backgroundColor: '#fff',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}
      >
        <HeadingSmall margin="0">Services</HeadingSmall>
        <Button
          onClick={handleNewService}
          startEnhancer={<Plus />}
          size={SIZE.compact}
        >
          Add Service
        </Button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#FFEBEE',
            color: '#C62828',
            padding: '16px 24px',
            marginBottom: '16px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}
        >
          <LabelMedium>{error}</LabelMedium>
        </div>
      )}

      <div
        style={{
          backgroundColor: 'transparent',
          borderTopStyle: 'none'
        }}
      >
        {store.servicesLoading ? (
          <div
            style={{
              backgroundColor: '#fff',
              padding: '16px 24px',
              marginBottom: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              textAlign: 'center'
            }}
          >
            <LabelMedium>Loading services...</LabelMedium>
          </div>
        ) : !error && (store.services || []).length === 0 ? (
          <div
            style={{
              backgroundColor: '#fff',
              padding: '16px 24px',
              marginBottom: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              textAlign: 'center'
            }}
          >
            <LabelMedium>No services found. Click "Add Service" to create one.</LabelMedium>
          </div>
        ) : !error && (store.services || []).map((service, index) => {
          const operations = store.serviceOperations?.[service.id] || [];

          return (
            <div
              key={service.id}
              style={{
                backgroundColor: '#fff',
                padding: '16px 24px',
                marginBottom: '16px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  marginBottom: '16px'
                }}
              >
                <HeadingSmall margin={0}>{service.name}</HeadingSmall>
                <div
                  style={{
                    display: 'flex',
                    gap: '4px'
                  }}
                >
                  <Button
                    onClick={(e) => handleDeleteService(e, service)}
                    kind={ButtonKind.tertiary}
                    shape={SHAPE.round}
                    size={SIZE.mini}
                    title="Delete Service"
                  >
                    <Delete size={20} />
                  </Button>
                  <Button
                    onClick={(e) => handleEditService(e, service)}
                    kind={ButtonKind.tertiary}
                    shape={SHAPE.round}
                    size={SIZE.mini}
                    title="Edit Service"
                  >
                    <Overflow size={20} />
                  </Button>
                </div>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '16px',
                  padding: '16px',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}
              >
                {operations.map((operation) => (
                  <div
                    key={operation.id}
                    style={{
                      backgroundColor: '#fff',
                      padding: '20px',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      border: '1px solid #E2E8F0',
                      ':hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        borderColor: '#CBD5E0'
                      }
                    }}
                    onClick={() => handleEditOperation(operation)}
                  >
                    <OperationCard
                      operation={operation}
                      onDelete={() => handleDeleteOperation(operation)}
                    />
                  </div>
                ))}
                <div
                  style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    transition: 'all 0.2s ease',
                    border: '2px dashed #E2E8F0',
                    ':hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      borderColor: '#CBD5E0',
                      backgroundColor: '#F8FAFC'
                    }
                  }}
                >
                  <NewOperationCard
                    onClick={() => handleNewOperation(service.id)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Outlet />
    </div>
  );
}
